<template>
  <div
    class="pb-wrapper"
    :class="rootStore.rootClasses"
  >
    <Transition>
      <div
        v-if="showFullLoader"
        class="pb-full-loader full-loader"
      >
        <Asset src="Images/Logos/SVG/Nicolaudie-profile-builder.svg" />
        <Spinner />
      </div>
    </Transition>
    <Transition>
      <OfflineModal v-if="!isOnline" />
    </Transition>
    <template v-if="isOnline && translationsLoaded">
      <PWABanner />
      <ReleaseNotesBanner />
      <BaseBanner
        v-if="removedBannerVisible"
        level="warning"
      >
        {{ translate('pb2.mainView.profileInTrashBannerMessage') }}
        <BaseButton
          v-if="canRestore"
          @click="profileStore.restore()"
        >
          {{ translate('pb2.mainView.profileInTrashBannerAction') }}
        </BaseButton>
      </BaseBanner>
      <BaseBanner
        v-if="readonlyBannerVisible"
        removable
        level="warning"
        @close="hideReadonlyBanner"
      >
        {{ translate('pb2.mainView.readonlyProfileBannerMessage') }}
        <BaseButton
          :to="{
            name: routeNames.Properties,
            query: { edit: 'permissions', requestWrite: true },
          }"
        >
          {{ translate('pb2.mainView.readonlyProfileBannerAction') }}
        </BaseButton>
      </BaseBanner>
      <template v-if="officialProfileBannerVisible">
        <BaseBanner
          v-if="canWrite"
          level="error"
        >
          <strong>Warning !</strong> This is an official profile
        </BaseBanner>
        <BaseBanner
          v-else
          level="warning"
        >
          {{ translate('pb2.mainView.officialProfileBannerMessage') }}
          <BaseButton @click="duplicateAndRedirect">
            {{ translate('pb2.mainView.officialProfileBannerAction') }}
          </BaseButton>
        </BaseBanner>
      </template>
      <template v-if="wolfmixReadonlyBannerVisible">
        <BaseBanner level="warning">
          {{ translate('pb2.mainView.wolfmixReadonlyBannerMessage') }}
          <BaseButton
            v-if="canCopy"
            @click="duplicateAndRedirect"
          >
            {{ translate('pb2.mainView.wolfmixReadonlyBannerActionCanCopy') }}
          </BaseButton>
          <BaseButton
            v-else
            :to="{
              name: routeNames.Properties,
              query: { edit: 'permissions', requestCopy: true },
            }"
          >
            {{ translate('pb2.mainView.wolfmixReadonlyBannerActionCannotCopy') }}
          </BaseButton>
          <!-- {{ translate('pb2.mainView.wolfmixReadonlyBannerMessage') }}
          <BaseButton @click="duplicateAndRedirect">
            {{ translate('pb2.mainView.wolfmixReadonlyBannerAction') }}
          </BaseButton> -->
        </BaseBanner>
      </template>
      <BaseBanner
        v-if="isOnline && supportSession"
        level="warning"
      >
        Support session active for request #{{ supportSession.requestId }}
        (ticket #{{ supportSession.ticketId }})
        <BaseButton
          class="actionMessage"
          @click="showSupportSessionDialog"
        >
          Request actions
        </BaseButton>
      </BaseBanner>
      <UiBanners
        :banners="visibleBanners"
        teleport="#pb-dialogs"
        @close="closeBanner"
      />
    </template>

    <RouterView :name="routeViews.Header" />
    <div class="pb-main">
      <ErrorView
        v-if="rootStore.errorPage"
        v-bind="rootStore.errorPage || {}"
        :show-header="!translationsLoaded"
      />
      <RouterView
        v-else-if="isOnline && loaded"
        :name="routeViews.Main"
      />
    </div>
    <Alerts
      position="alerter right bottom"
      group="main"
    />
    <EnvWatermark />
    <SupportSessionDialog
      v-if="isOnline && !showFullLoader"
      v-bind="supportSessionControl"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  mergeProps,
} from 'vue'

import Alerts from '@nicolaudie-group/ng-ui-kit/components/Alerts/Alerts.vue'
import Spinner from '@nicolaudie-group/ng-ui-kit/components/Spinner/Spinner.vue'
import Asset from '@nicolaudie-group/ng-ui-kit/components/Asset/Asset.vue'
import BaseBanner from '@nicolaudie-group/ng-ui-kit/components/BaseBanner/BaseBanner.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import UiBanners from '@nicolaudie-group/ng-ui-kit/components/UiBanners/UiBanners.vue'

import useToggle from '@nicolaudie-group/ng-ui-kit/composables/useToggle'
import useAlerter from '@nicolaudie-group/ng-ui-kit/composables/useAlerter'
import useVersionnedBanners from '@nicolaudie-group/ng-ui-kit/composables/useVersionnedBanners'

import setTranslate from '@nicolaudie-group/ng-ui-kit/helpers/useTranslate'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import { warn } from '@nicolaudie-group/ng-ui-kit/helpers/prod'
import isIn from '@nicolaudie-group/ng-ui-kit/helpers/isIn'
import { alertsToBanners } from '@nicolaudie-group/ng-ui-kit/helpers/alertToBanner'
import useDelay from '@nicolaudie-group/ng-ui-kit/composables/useDelay'
import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'

import PbError from '@/classes/PbError.class'

import { SDKTypes } from '@/helpers/sdk'
import translateHelper from '@/helpers/translate'

import routeViews from '@/router/views'
import routeNames from '@/router/names'

import useRootStore from '@/stores/root.store'
import useUserStore from '@/stores/user.store'
import useRouterStore from '@/stores/router.store'
import useProfileStore from '@/stores/profile.store'
import useSupportStore from '@/stores/support.store'

import useTranslate from '@/composables/useTranslate'
import { useProfile } from '@/composables/useProfile'
import useLogin from '@/composables/useLogin'

import ErrorView from '@/views/ErrorView.vue'

import EnvWatermark from '@nicolaudie-group/ng-ui-kit/components/Watermark/EnvWatermark.vue'
import usePbTitle from '@/composables/usePbTitle'
import useDialog from './composables/useDialog'
import SupportSessionDialog from './components/SupportSessionDialog/SupportSessionDialog.vue'
import PWABanner from './components/PWABanner.vue'
import ReleaseNotesBanner from './components/ReleaseNotesBanner.vue'
import OfflineModal from './components/OfflineModal.vue'
import { useOnline } from '@vueuse/core'

const { delay, reset } = useDelay()

const rootStore = useRootStore()
const userStore = useUserStore()
const routerStore = useRouterStore()
const profileStore = useProfileStore()
const supportStore = useSupportStore()

// Give the translate method to the UI kit
setTranslate(translateHelper)
const { translationsLoaded, translate } = useTranslate()

// Update the page title when the route changes
usePbTitle()

const isOnline = useOnline()

const showFullLoader = ref(true)
const loaded = computed(() => routerStore.route.meta.loaded && routerStore.route.meta.loaded())
const failed = computed(() => routerStore.route.meta.failed && routerStore.route.meta.failed())

watch(loaded, () => {
  reset()
  if (loaded.value) {
    delay(() => {
      showFullLoader.value = false
    }, 500)
  } else {
    showFullLoader.value = true
  }
}, { immediate: true })

const login = useLogin()

const { error } = useAlerter()

const lastError = computed(() => rootStore.error)

watch(failed, () => {
  // If there route failed to load it's mandatory data
  if (failed.value) {
    showFullLoader.value = false
    // If there is no error, add a default one to avoid a blank page
    if (!rootStore.errorPage) {
      // Special case for 403 errors, we want to show the "forbidden" error page instead of
      // a generic error page (so that the AccessRequestForm can be displayed).
      // So we do not modify the error payload, we just add the "page" flag to it.
      //
      // Other errors will get a generic "failed to load" title and message, but will keep
      // the original error's meta, status, etc.
      // See profile.store.js for more details (in the "profile download" action meta).
      const errorPayload = rootStore.error?.status === SDKTypes.HttpStatusCode.FORBIDDEN ? {
        ...rootStore.error,
        page: true,
      } : {
        // Clone the last error if any, to retrieve it's meta, status, etc.
        ...rootStore.error,
        // And add a generic "failed to load" title and message
        title: 'Failed to load',
        message: 'Profile Builder could not load properly',
        page: true,
      }
      rootStore.addError(new PbError(errorPayload))
    }
  } else {
    rootStore.clearErrorPages()
  }
}, { immediate: true })

watch(lastError, (errorData) => {
  if (errorData) {
    const {
      title, message, page, status,
    } = errorData
    if (!page) {
      error({
        title,
        text: message,
      })
    }

    if (status === SDKTypes.HttpStatusCode.UNAUTHORIZED) {
      login()
    }
  }
})


const profile = useProfile()

const canRead = computed(() => !!profile.value)
const canWrite = computed(() => canRead.value && profile.value.userCanEdit)
const canCopy = computed(() => canRead.value && profile.value.userCanCopy)
const canRestore = computed(() => (
  canRead.value
  && profile.value.userCanDelete
  && profile.value.isRemove
))
const { off: hideReadonlyBanner, state: readonlyBannerState } = useToggle(true)

const removedBannerVisible = computed(() => (
  profile.value
  && profile.value.isRemove
  && !routerStore.route?.meta?.hideProfileBanners
))

const officialProfileBannerVisible = computed(() => (
  canRead.value
  && profile.value.isCertificated
  && !routerStore.route?.meta?.hideProfileBanners
))

const wolfmixReadonlyBannerVisible = computed(() => (
  canRead.value
  && !removedBannerVisible.value
  && !officialProfileBannerVisible.value
  && !routerStore.route?.meta?.hideProfileBanners
  && profile.value.importedFromWolfmix
))

const readonlyBannerVisible = computed(() => (
  canRead.value
  && !canWrite.value
  && !removedBannerVisible.value
  && !wolfmixReadonlyBannerVisible.value
  && !routerStore.route?.meta?.hideProfileBanners
  && !profile.value.isCertificated
  && readonlyBannerState.value
))

// Add a shortcut to toggle dev logs & variables exposition bypass
const { active: bPressed } = useHotkeys(KEYS.B, {
  shift: false,
  alt: false,
  meta: false,
  ctrl: false,
  exact: true,
  disableInInput: true,
  disableInTextarea: true,
  disableInButton: true,
})

const banners = ref([])
watch(() => userStore.apiInfos?.alerts, (alerts) => {
  banners.value = alertsToBanners(alerts || [])
})

const supportSession = computed(() => supportStore.session)

const {
  control: supportSessionControl,
  open: showSupportSessionDialog,
} = useDialog({
  initial: false,
  query: 'support-session',
})

const {
  visibleBanners,
  closeBanner,
} = useVersionnedBanners(banners)

watch(bPressed, (value) => {
  if (!isIn.prod && value) {
    dev.configure({ bypass: !dev.bypass })
    warn(`Bypass ${dev.bypass ? 'enabled' : 'disabled'}`)
  }
})

watch(() => rootStore.rootClasses, (newRootClasses) => {
  const dialogsProps = mergeProps({ class: newRootClasses })
  document.getElementById('pb-dialogs').classList = dialogsProps.class
}, { immediate: true, deep: true })

async function duplicateAndRedirect() {
  const { uid } = await profileStore.duplicate()
  await routerStore.push({
    name: routeNames.Properties,
    params: {
      profileUid: uid,
    },
  })
}
</script>

<style lang="scss" src="./App.scss" />
